import { isApiError } from "@rekati/api";
import { useI18n } from "../../content/i18n";

/**
 * One sentence for a failed call, in the console's language. A page passes
 * the codes it knows (`{ LOGIN_TAKEN: t("users.loginTaken") }`); the shared
 * ones follow; anything else is the generic line. A 409 whose reasons live in
 * the backend's `message` (CAMPAIGN_NOT_READY, TEMPLATE_INVALID) is shown by
 * the page itself, with that message under its own sentence.
 */
export function useStaffError() {
  const { t } = useI18n();
  return (error: unknown, known: Record<string, string> = {}): string => {
    if (!isApiError(error)) return t("errors.generic");
    const own = known[error.code];
    if (own) return own;
    if (error.isNetwork || error.code === "TIMEOUT") return t("errors.network");
    switch (error.code) {
      case "FORBIDDEN":
        return t("errors.forbidden");
      case "UNAUTHORIZED":
        return t("errors.sessionExpired");
      case "RATE_LIMITED":
        return t("errors.rateLimited", { s: error.retryAfterSec ?? 60 });
      case "EVENT_NOT_CONFIGURED":
        return t("errors.eventNotConfigured");
      case "VALIDATION_FAILED":
        return t("errors.validation");
      default:
        return t("errors.generic");
    }
  };
}

/** `details[]` of a 400 as `{ field: code }`, for mapping onto a form's inputs. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  return Object.fromEntries(error.details.map((d) => [d.field, d.code]));
}

/** The sentence under a field for a detail code (docs/api.md 37.1). */
export function useFieldMessage() {
  const { t } = useI18n();
  return (code: string | undefined): string | null => {
    if (!code) return null;
    switch (code) {
      case "REQUIRED":
        return t("fields.required");
      case "TOO_LONG":
        return t("fields.tooLong");
      case "TOO_SHORT":
        return t("fields.tooShort");
      case "PHONE_INVALID":
        return t("fields.phone");
      case "EMAIL_TAKEN":
        return t("fields.emailTaken");
      case "PHONE_TAKEN":
        return t("fields.phoneTaken");
      case "FIELD_NOT_APPLICABLE":
        return t("fields.notApplicable");
      default:
        return t("fields.invalid");
    }
  };
}
